// Small helpers to build CSV / Markdown blobs and trigger a browser download.

function csvCell(v) {
    if (v == null) return "";
    let s = Array.isArray(v) ? v.join("; ") : String(v);
    if (/[",\n\r]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
    return s;
}

export function downloadBlob(content, filename, mime) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const FIRM_COLS = [
    ["name", "Name"], ["industry", "Industry"], ["region", "Region"], ["headquarters", "HQ"],
    ["founded_year", "Founded"], ["company_size", "Size"], ["website", "Website"],
    ["competitors", "Competitors"], ["tags", "Tags"], ["overview", "Overview"], ["notes", "Notes"],
];

export function firmsToCSV(firms) {
    const header = [...FIRM_COLS.map(([, l]) => l), "Products", "Total revenue (USD)"].map(csvCell).join(",");
    const rows = firms.map((f) => {
        const products = (f.products || []).map((p) => `${p.name} ($${Number(p.annual_revenue_usd || 0).toFixed(0)})`).join(" | ");
        const totalRevenue = (f.products || []).reduce((s, p) => s + (Number(p.annual_revenue_usd) || 0), 0);
        return [...FIRM_COLS.map(([k]) => csvCell(f[k])), csvCell(products), csvCell(totalRevenue.toFixed(0))].join(",");
    });
    return [header, ...rows].join("\n");
}

export function firmsToMarkdown(firms) {
    const lines = ["# Affynix Library Export", "", `Exported ${new Date().toISOString()}`, "", `Total firms: **${firms.length}**`, ""];
    for (const f of firms) {
        const totalRevenue = (f.products || []).reduce((s, p) => s + (Number(p.annual_revenue_usd) || 0), 0);
        lines.push(`## ${f.name}`);
        lines.push("");
        lines.push(`- **Industry**: ${f.industry || "—"}`);
        lines.push(`- **Region**: ${f.region || "—"}`);
        lines.push(`- **Headquarters**: ${f.headquarters || "—"}`);
        lines.push(`- **Founded**: ${f.founded_year || "—"}`);
        lines.push(`- **Size**: ${f.company_size || "—"}`);
        if (f.website) lines.push(`- **Website**: ${f.website}`);
        if ((f.tags || []).length) lines.push(`- **Tags**: ${(f.tags || []).join(", ")}`);
        if ((f.competitors || []).length) lines.push(`- **Competitors**: ${(f.competitors || []).join(", ")}`);
        lines.push("");
        if (f.overview) { lines.push(`### Overview`); lines.push(""); lines.push(f.overview); lines.push(""); }
        if (f.notes) { lines.push(`### Notes`); lines.push(""); lines.push(f.notes); lines.push(""); }
        if ((f.products || []).length) {
            lines.push(`### Products (${f.products.length}) — Total: $${totalRevenue.toLocaleString()}`);
            lines.push("");
            lines.push("| Product | Revenue (USD) | Growth | Share |");
            lines.push("|---|---:|---:|---:|");
            for (const p of f.products) {
                lines.push(`| ${p.name} | $${Number(p.annual_revenue_usd || 0).toLocaleString()} | ${Number(p.growth_pct || 0).toFixed(1)}% | ${Number(p.revenue_share_pct || 0).toFixed(0)}% |`);
            }
            lines.push("");
            // Monthly revenue series per product (if available)
            for (const p of f.products) {
                if (!(p.monthly_revenue || []).length) continue;
                lines.push(`#### ${p.name} — Monthly revenue`);
                lines.push("");
                lines.push("| Month | Revenue (USD) |");
                lines.push("|---|---:|");
                for (const m of p.monthly_revenue) {
                    lines.push(`| ${m.month} | $${Number(m.revenue || 0).toLocaleString()} |`);
                }
                lines.push("");
            }
        }
        lines.push("---");
        lines.push("");
    }
    return lines.join("\n");
}

const SYN_COLS = [
    ["firm_a_name", "Firm A"], ["product_name", "Product"], ["firm_b_name", "Firm B"],
    ["fit_score", "Fit"], ["projected_revenue", "Projected revenue (USD)"], ["pinned", "Pinned"], ["created_at", "Created"],
];

export function synergiesToCSV(synergies) {
    const header = SYN_COLS.map(([, l]) => l).map(csvCell).join(",");
    const rows = synergies.map((s) => SYN_COLS.map(([k]) => csvCell(s[k])).join(","));
    return [header, ...rows].join("\n");
}

export function synergyToMarkdown(r) {
    const lines = [];
    lines.push(`# Synergy · ${r.firm_a.name} → ${r.firm_b.name}`);
    lines.push("");
    lines.push(`**Product**: ${r.product.name}`);
    lines.push(`**Fit score**: ${r.fit_score}/100`);
    lines.push(`**Projected revenue at ${r.firm_b.name}**: $${Number(r.estimated_added_annual_revenue_usd || 0).toLocaleString()} (${Number(r.estimated_revenue_share_pct || 0).toFixed(1)}% of total)`);
    lines.push("");
    if (r.relationship_summary) {
        lines.push(`## Current relationship`);
        lines.push("");
        lines.push(`${r.existing_relationship ? "✅ ACTIVE" : "⚪ NONE KNOWN"} — ${r.relationship_summary}`);
        if ((r.existing_products_from_a_used_by_b || []).length) {
            lines.push("");
            lines.push(`Products already used: ${r.existing_products_from_a_used_by_b.join(", ")}`);
        }
        lines.push("");
    }
    if (r.rationale) { lines.push(`## Rationale`); lines.push(""); lines.push(r.rationale); lines.push(""); }
    if ((r.use_cases || []).length) { lines.push(`## Use cases`); lines.push(""); r.use_cases.forEach((u) => lines.push(`- ${u}`)); lines.push(""); }
    if ((r.integration_notes || []).length) { lines.push(`## Integration notes`); lines.push(""); r.integration_notes.forEach((u) => lines.push(`- ${u}`)); lines.push(""); }
    if ((r.risks || []).length) { lines.push(`## Risks`); lines.push(""); r.risks.forEach((u) => lines.push(`- ${u}`)); lines.push(""); }
    if ((r.monthly_revenue_projection || []).length) {
        lines.push(`## 12-month projection at ${r.firm_b.name}`);
        lines.push("");
        lines.push("| Month | Revenue (USD) |");
        lines.push("|---|---:|");
        r.monthly_revenue_projection.forEach((m) => lines.push(`| ${m.month} | $${Number(m.revenue || 0).toLocaleString()} |`));
        lines.push("");
    }
    return lines.join("\n");
}
